// Lapisan data HADIAH PEMBELIAN (mig 8680) — pembaca storefront, RPC admin,
// dan info pembelian berhadiah untuk layar pesanan (mig 8850). Aturan &
// hitungan TIDAK di sini: `src/lib/gift.ts`, `src/lib/giftOrders.ts`.

import {
  GIFT_RULE_SELECT,
  giftRuleCoversProduct,
  giftRuleHasQuota,
  isGiftRuleRunning,
  mapGiftQuotaRow,
  mapGiftRuleRow,
  type GiftProductRef,
  type GiftQuota,
  type GiftRule
} from "@/lib/gift";
import {
  adminGiftErrorMessage,
  adminGiftOffers,
  mapAdminGiftOfferRow,
  mapAdminGiftPurchaseRow,
  mapAdminGiftQuotaRow,
  type AdminGiftOffer,
  type AdminGiftPurchase,
  type adminGiftPayload
} from "@/lib/giftAdmin";
import {
  EMPTY_GIFT_DATA,
  mapGiftCarryRow,
  mapGiftOpenPurchaseRow,
  type GiftCarry,
  type GiftOpenPurchase,
  type GiftProductInfo,
  type StorefrontGiftData
} from "@/lib/giftCart";
import {
  giftRowReducible,
  mapGiftAdjustNote,
  mapGiftFillResult,
  mapOrderGiftRule,
  type GiftAdjustNote,
  type GiftFillResult,
  type OrderGiftRule
} from "@/lib/giftOrders";
import type { AdminGiftRow } from "@/lib/adminCart";
import { CART_INVOICE_ACTIVE_STATUSES } from "@/lib/orderCart";
import type { GiftListProduct, GiftProductFacts } from "@/lib/giftDraft";
import {
  GIFT_PRODUCT_SELECT,
  GIFT_VARIANT_CAP,
  GIFT_VARIANT_SELECT,
  mapGiftProduct,
  mapGiftRewardVariant
} from "@/lib/giftIndex";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";

// ─── Storefront (anon / pembeli) ─────────────────────────────────────────────
// Kolom & pemeta produk/varian hadiah dipakai bersama pembaca server
// (`giftServer.ts`) — satu sumber di `src/lib/giftIndex.ts`.

/**
 * Aturan hadiah yang SEDANG BERJALAN dan mencakup produk-produk keranjang ini
 * — produk tunggal (Tahap 1) maupun bertarget produk / kategori / merek / semua
 * (mig 8780) — beserta produk isi keranjang (merek, kategori, tipe, varian),
 * produk hadiah & variannya, `option_ids` varian baris keranjang produk syarat
 * produk tunggal (`cartVariantIds`), dan sisa kuota aturan berkuota (mig 8740).
 * Aturan yang hadiahnya tak terbaca TETAP ikut: ia tetap memiliki barisnya di
 * server (H24), jadi aturan yang lebih muda tak boleh menghitungnya.
 *
 * FAIL-CLOSED terhadap toko (aturan A2): toko tak ter-resolusi = tanpa aturan,
 * tak pernah query tanpa filter. Galat query DILEMPAR — pemanggil memutuskan
 * (keranjang memajang "Coba lagi").
 */
export async function fetchStorefrontGiftData(
  productIds: readonly string[],
  opts: { now?: Date; cartVariantIds?: readonly string[] } = {}
): Promise<StorefrontGiftData> {
  const wanted = [...new Set(productIds.filter(Boolean))];
  if (!wanted.length) return EMPTY_GIFT_DATA;
  const storeId = getClientStoreId();
  if (!storeId) return EMPTY_GIFT_DATA;
  const db = createBrowserSupabase("store");
  if (!db) return EMPTY_GIFT_DATA;
  const now = opts.now ?? new Date();

  const { data: ruleRows, error: ruleErr } = await db
    .from("gift_rules")
    .select(GIFT_RULE_SELECT)
    .eq("store_id", storeId)
    .eq("is_active", true);
  if (ruleErr) throw new Error(ruleErr.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const running = ((ruleRows as any[]) ?? [])
    .map(mapGiftRuleRow)
    .filter((r) => r.storeId === storeId && isGiftRuleRunning(r, now));
  if (!running.length) return EMPTY_GIFT_DATA;

  // Produk isi keranjang: merek/kategori/tipe = cakupan aturan bertarget.
  const products = new Map<string, GiftProductInfo>();
  const loadProducts = async (ids: readonly string[]) => {
    for (let i = 0; i < ids.length; i += 150) {
      const { data: rows, error } = await db
        .from("products")
        .select(GIFT_PRODUCT_SELECT)
        .in("id", ids.slice(i, i + 150))
        .eq("store_id", storeId);
      if (error) throw new Error(error.message);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const row of (rows as any[]) ?? []) {
        const p = mapGiftProduct(row);
        products.set(p.id, p);
      }
    }
  };
  await loadProducts(wanted);
  const cartRefs: GiftProductRef[] = wanted
    .map((id) => products.get(id))
    .filter((p): p is GiftProductInfo => Boolean(p))
    .map((p) => ({ id: p.id, brand: p.brand, category: p.category, productType: p.productType }));
  const rules = running
    .filter((r) => cartRefs.some((ref) => giftRuleCoversProduct(r, ref)))
    .sort((a, b) => a.name.localeCompare(b.name, "id"));
  if (!rules.length || !rules.some((r) => r.rewardProductId)) return EMPTY_GIFT_DATA;

  const rewardIds = [...new Set(rules.map((r) => r.rewardProductId).filter((x): x is string => Boolean(x)))];
  await loadProducts(rewardIds.filter((id) => !products.has(id)));

  // Varian produk hadiah (RLS toko = varian aktif produk aktif).
  const { data: rewardRows, error: rewardErr } = await db
    .from("product_variants")
    .select(GIFT_VARIANT_SELECT)
    .in("product_id", rewardIds)
    .eq("store_id", storeId)
    .order("sort_order", { ascending: true })
    .limit(GIFT_VARIANT_CAP);
  if (rewardErr) throw new Error(rewardErr.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const v of (rewardRows as any[]) ?? []) {
    const p = products.get(String(v.product_id));
    if (!p) continue;
    p.variants.push(mapGiftRewardVariant(v, p.productType));
  }

  // `option_ids` varian baris keranjang produk syarat PRODUK TUNGGAL (batas
  // varian); aturan bertarget mencakup seluruh varian.
  const variantOptionIds = new Map<string, string[]>();
  const cartIds = [...new Set((opts.cartVariantIds ?? []).filter(Boolean))];
  const triggerSet = new Set(
    rules.filter((r) => r.triggerScope !== "target" && r.triggerProductId).map((r) => r.triggerProductId as string)
  );
  for (let i = 0; triggerSet.size && i < cartIds.length; i += 200) {
    const { data: rows, error } = await db
      .from("product_variants")
      .select("id, product_id, option_ids")
      .in("id", cartIds.slice(i, i + 200))
      .eq("store_id", storeId);
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const v of (rows as any[]) ?? []) {
      if (!triggerSet.has(String(v.product_id))) continue;
      variantOptionIds.set(String(v.id), Array.isArray(v.option_ids) ? v.option_ids.map(String) : []);
    }
  }

  // Sisa kuota aturan BERKUOTA (mig 8740) — hanya angka sisa; jatah pembeli
  // yang login diturunkan server dari sesinya, bukan dari argumen.
  const quotas = new Map<string, GiftQuota>();
  const quotaIds = rules.filter(giftRuleHasQuota).map((r) => r.id);
  if (quotaIds.length) {
    const { data: quotaRows, error: quotaErr } = await db.rpc("gift_rule_quota", { p_rule_ids: quotaIds });
    if (quotaErr) throw new Error(quotaErr.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (quotaRows as any[]) ?? []) {
      const { ruleId, quota } = mapGiftQuotaRow(row);
      if (ruleId) quotas.set(ruleId, quota);
    }
  }
  return { rules, products, variantOptionIds, quotas };
}

/**
 * Sisa pesanan sebelumnya milik pembeli yang LOGIN, per aturan (`my_gift_carry`,
 * mig 8880, H55/H59): pcs, pcs berharga, dan nilai barang tak terikat yang
 * dimiliki aturan itu (H24). Toko diturunkan server dari aturannya dan
 * pelanggannya dari sesi — tanpa login = kosong. `excludeOrderId` = pesanan
 * yang sedang diedit (barisnya dihitung dari editor). Galat dilempar.
 */
export async function fetchMyGiftCarry(
  ruleIds: readonly string[],
  excludeOrderId: string | null = null
): Promise<Map<string, GiftCarry>> {
  const out = new Map<string, GiftCarry>();
  const ids = [...new Set(ruleIds.filter(Boolean))].slice(0, 200);
  if (!ids.length) return out;
  const db = createBrowserSupabase("store");
  if (!db) return out;
  const { data, error } = await db.rpc("my_gift_carry", {
    p_rule_ids: ids,
    p_exclude_order_id: excludeOrderId
  });
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const row of (data as any[]) ?? []) {
    const mapped = mapGiftCarryRow(row);
    if (mapped) out.set(mapped.ruleId, mapped.carry);
  }
  return out;
}

/**
 * Pembelian LAMA pembeli yang login yang masih bisa bertambah + sisa barang
 * tak terikatnya yang dicakup tiap snapshot (`my_gift_open_purchases`, Hadiah
 * Tahap 6-2) — bahan keranjang belanja & pratinjau Edit Pesanan. Urut tertua
 * dulu. Toko diturunkan server dari aturannya; tanpa login = kosong. Galat
 * dilempar (pemanggil: dihitung tanpa pembelian lama).
 */
export async function fetchMyGiftOpenPurchases(
  ruleIds: readonly string[],
  excludeOrderId: string | null = null
): Promise<GiftOpenPurchase[]> {
  const ids = [...new Set(ruleIds.filter(Boolean))].slice(0, 200);
  if (!ids.length) return [];
  const db = createBrowserSupabase("store");
  if (!db) return [];
  const { data, error } = await db.rpc("my_gift_open_purchases", {
    p_rule_ids: ids,
    p_exclude_order_id: excludeOrderId
  });
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map(mapGiftOpenPurchaseRow).filter((p): p is GiftOpenPurchase => p !== null);
}

/**
 * Pembelian berhadiah satu pelanggan untuk pratinjau admin
 * (`admin_gift_customer_purchases`, Hadiah Tahap 6-2): hak & pcs hidup,
 * penanda bisa bertambah, dan id baris tak terikat yang dicakup snapshot-nya.
 * Sesi ADMIN; toko dinilai server. Galat dilempar — pratinjaunya saja yang
 * kembali ke perilaku lama.
 */
export async function fetchAdminGiftCustomerPurchases(customerId: string): Promise<AdminGiftPurchase[]> {
  const db = createBrowserSupabase("admin");
  if (!db || !customerId) return [];
  const { data, error } = await db.rpc("admin_gift_customer_purchases", { p_customer_id: customerId });
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map(mapAdminGiftPurchaseRow).filter((p): p is AdminGiftPurchase => p !== null);
}

/**
 * Checkout jalur LANGSUNG (baris ditulis klien, tanpa RPC checkout) → gabungan
 * hadiah lintas pesanan (mig 8880, H54): server menilai pesanan pembeli yang
 * barisnya ditulis ≤ 15 menit lalu bersama sisa pesanan sebelumnya. Galat
 * dilempar; pemanggil memperlakukannya best-effort (pesanannya sudah jadi).
 */
export async function attachMyCheckoutGifts(): Promise<void> {
  const db = createBrowserSupabase("store");
  if (!db) return;
  const { error } = await db.rpc("attach_my_checkout_gifts");
  if (error) throw new Error(error.message);
}

/**
 * Nama aturan pembelian berhadiah (snapshot `order_gifts.rule_name`) per id —
 * label baris pesanan. Sesi pembeli membaca MILIKNYA (RLS "own read"); sesi
 * admin membaca tokonya ("admin read").
 */
export async function fetchOrderGiftNames(
  ids: readonly string[],
  scope?: "admin"
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const unique = [...new Set(ids.filter(Boolean))];
  if (!unique.length) return out;
  const db = scope === "admin" ? createBrowserSupabase("admin") : createBrowserSupabase();
  if (!db) return out;
  for (let i = 0; i < unique.length; i += 200) {
    const { data, error } = await db
      .from("order_gifts")
      .select("id, rule_name")
      .in("id", unique.slice(i, i + 200));
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (data as any[]) ?? []) {
      out.set(String(row.id), String(row.rule_name ?? "").trim() || "Hadiah");
    }
  }
  return out;
}

/** Satu pembelian berhadiah untuk layar pesanan (mig 8680/8850). */
export type OrderGiftInfo = {
  name: string;
  /** Snapshot aturan saat pembelian — bahan hak & pratinjau Edit Pesanan. */
  rule: OrderGiftRule;
  /** Penggeseran hadiah terakhir (`adjust_note`, mig 8850); null = belum pernah. */
  adjustNote: GiftAdjustNote | null;
  /** Umur pembelian — urutan pembagian pcs saat Edit Pesanan (tertua dulu). */
  createdAt: string;
};

/**
 * Pembelian berhadiah per id: nama, snapshot aturan, catatan penggeseran
 * terakhir. Sesi pembeli membaca MILIKNYA (RLS "own read"); sesi admin membaca
 * tokonya ("admin read"). `adjust_note` = kolom mig 8850 — migrasinya WAJIB
 * mendahului merge.
 */
export async function fetchOrderGiftInfo(
  ids: readonly string[],
  scope?: "admin"
): Promise<Map<string, OrderGiftInfo>> {
  const out = new Map<string, OrderGiftInfo>();
  const unique = [...new Set(ids.filter(Boolean))];
  if (!unique.length) return out;
  const db = scope === "admin" ? createBrowserSupabase("admin") : createBrowserSupabase();
  if (!db) return out;
  for (let i = 0; i < unique.length; i += 200) {
    const { data, error } = await db
      .from("order_gifts")
      .select("id, rule_name, rule, adjust_note, created_at")
      .in("id", unique.slice(i, i + 200));
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (data as any[]) ?? []) {
      out.set(String(row.id), {
        name: String(row.rule_name ?? "").trim() || "Hadiah",
        rule: mapOrderGiftRule(row.rule),
        adjustNote: mapGiftAdjustNote(row.adjust_note),
        createdAt: String(row.created_at ?? "")
      });
    }
  }
  return out;
}

// ─── Admin ───────────────────────────────────────────────────────────────────

const GIFT_EDIT_ROW_SELECT =
  "id, order_id, variant_id, variant_name, qty, unit_price, voucher_discount_per_pcs, keep_status, keep_source, " +
  "item_status, paid_qty, order_gift_id, gift_role, orders(status)";

/**
 * Bahan pratinjau hadiah editor Detail PO (Gabung Pesanan, mig 8890, H60):
 * baris hidup pesanan-pesanan itu + SELURUH baris pembelian berhadiah yang
 * barang syarat / hadiahnya ada di sana (lintas pesanan — hak hidup dihitung
 * dari semuanya), beserta snapshot aturannya. Sesi ADMIN. Galat dilempar —
 * editor tetap jalan tanpa pratinjau.
 */
export async function fetchGiftEditRowsAdmin(
  orderIds: readonly string[]
): Promise<{ rows: AdminGiftRow[]; gifts: Map<string, OrderGiftInfo> }> {
  const empty = { rows: [] as AdminGiftRow[], gifts: new Map<string, OrderGiftInfo>() };
  const ids = [...new Set(orderIds.filter(Boolean))];
  const db = createBrowserSupabase("admin");
  if (!db || !ids.length) return empty;
  const { data: own, error: ownErr } = await db.from("order_items").select(GIFT_EDIT_ROW_SELECT).in("order_id", ids);
  if (ownErr) throw new Error(ownErr.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const byId = new Map<string, any>(((own as any[]) ?? []).map((r) => [String(r.id), r]));
  const purchaseIds = [
    ...new Set([...byId.values()].map((r) => (r.order_gift_id ? String(r.order_gift_id) : "")).filter(Boolean))
  ];
  if (!purchaseIds.length) return empty;
  for (let i = 0; i < purchaseIds.length; i += 200) {
    const { data, error } = await db
      .from("order_items")
      .select(GIFT_EDIT_ROW_SELECT)
      .in("order_gift_id", purchaseIds.slice(i, i + 200));
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const r of (data as any[]) ?? []) byId.set(String(r.id), r);
  }
  // Baris hadiah di tagihan aktif tak bisa diturunkan (cermin `gift_adjust`).
  const giftRowIds = [...byId.values()].filter((r) => r.gift_role === "hadiah").map((r) => String(r.id));
  const inActiveInvoice = new Set<string>();
  for (let i = 0; i < giftRowIds.length; i += 200) {
    const { data, error } = await db
      .from("invoice_lines")
      .select("order_item_id, invoices!inner(status)")
      .in("order_item_id", giftRowIds.slice(i, i + 200))
      .is("cancelled_at", null)
      .in("invoices.status", CART_INVOICE_ACTIVE_STATUSES);
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const l of (data as any[]) ?? []) if (l.order_item_id) inActiveInvoice.add(String(l.order_item_id));
  }
  const rows: AdminGiftRow[] = [...byId.values()].map((i) => ({
    itemId: String(i.id),
    orderId: String(i.order_id),
    variantId: i.variant_id ? String(i.variant_id) : null,
    variantName: String(i.variant_name ?? ""),
    qty: Number(i.qty ?? 0),
    unitPrice: i.unit_price == null ? null : Number(i.unit_price),
    voucherDiscountPerPcs: i.voucher_discount_per_pcs == null ? null : Number(i.voucher_discount_per_pcs),
    keepStatus: (i.keep_status ?? null) as string | null,
    itemStatus: (i.item_status ?? null) as string | null,
    orderGiftId: i.order_gift_id ? String(i.order_gift_id) : null,
    giftRole: (i.gift_role ?? null) as string | null,
    orderCancelled: i.orders?.status === "Batal",
    locked:
      i.gift_role === "hadiah" &&
      !giftRowReducible({
        keepStatus: i.keep_status ?? null,
        keepSource: i.keep_source ?? null,
        itemStatus: i.item_status ?? null,
        paidQty: Number(i.paid_qty ?? 0),
        inActiveInvoice: inActiveInvoice.has(String(i.id))
      })
  }));
  return { rows, gifts: await fetchOrderGiftInfo(purchaseIds, "admin") };
}

// ─── Tambah Hadiah keranjang admin (Tahap 3A, mig 8830) ──────────────────────

/**
 * Tawaran Tambah Hadiah untuk satu pelanggan: baris syarat milik setiap aturan
 * (`admin_gift_offer_rows` — pemilik H24 + periode per waktu baris dipesan,
 * level, belum terikat hadiah/bundel), aturannya, produk syarat produk tunggal
 * & produk hadiah beserta varian AKTIF-nya, dan sisa kuota pelanggan ini
 * (`admin_gift_rule_quota`). Sesi ADMIN; toko dinilai server. Galat dilempar —
 * keranjang admin tetap tampil tanpa tawaran.
 */
export async function fetchAdminGiftOffers(customerId: string): Promise<AdminGiftOffer[]> {
  const db = createBrowserSupabase("admin");
  if (!db || !customerId) return [];
  const { data: rowData, error: rowErr } = await db.rpc("admin_gift_offer_rows", { p_customer_id: customerId });
  if (rowErr) throw new Error(rowErr.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows = ((rowData as any[]) ?? []).map(mapAdminGiftOfferRow).filter((r) => r.ruleId && r.itemId);
  if (!rows.length) return [];

  const ruleIds = [...new Set(rows.map((r) => r.ruleId))];
  const { data: ruleRows, error: ruleErr } = await db.from("gift_rules").select(GIFT_RULE_SELECT).in("id", ruleIds);
  if (ruleErr) throw new Error(ruleErr.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rules = ((ruleRows as any[]) ?? []).map(mapGiftRuleRow);
  if (!rules.length) return [];

  const rewardIds = [...new Set(rules.map((r) => r.rewardProductId).filter((x): x is string => Boolean(x)))];
  const productIds = [
    ...new Set([
      ...rewardIds,
      ...rules
        .filter((r) => r.triggerScope !== "target" && r.triggerProductId)
        .map((r) => r.triggerProductId as string)
    ])
  ];
  const products = new Map<string, GiftProductInfo>();
  for (let i = 0; i < productIds.length; i += 150) {
    const { data: prodRows, error } = await db
      .from("products")
      .select(GIFT_PRODUCT_SELECT)
      .in("id", productIds.slice(i, i + 150));
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (prodRows as any[]) ?? []) {
      const prod = mapGiftProduct(row);
      products.set(prod.id, prod);
    }
  }
  if (rewardIds.length) {
    // Sesi admin membaca varian NONAKTIF juga — disaring di sini (server menolaknya).
    const { data: varRows, error: varErr } = await db
      .from("product_variants")
      .select(GIFT_VARIANT_SELECT)
      .in("product_id", rewardIds)
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .limit(GIFT_VARIANT_CAP);
    if (varErr) throw new Error(varErr.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const v of (varRows as any[]) ?? []) {
      const prod = products.get(String(v.product_id));
      if (prod) prod.variants.push(mapGiftRewardVariant(v, prod.productType));
    }
  }

  const quotas = new Map<string, GiftQuota>();
  const quotaIds = rules.filter(giftRuleHasQuota).map((r) => r.id);
  if (quotaIds.length) {
    const { data: quotaRows, error: quotaErr } = await db.rpc("admin_gift_rule_quota", {
      p_rule_ids: quotaIds,
      p_customer_id: customerId
    });
    if (quotaErr) throw new Error(quotaErr.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (quotaRows as any[]) ?? []) {
      const { ruleId, quota } = mapAdminGiftQuotaRow(row);
      if (ruleId) quotas.set(ruleId, quota);
    }
  }
  return adminGiftOffers({ rules, rows, products, quotas });
}

// ─── Kasir (mig 8840) ────────────────────────────────────────────────────────

/** Bahan kartu hadiah Kasir: data aturan + slot level pelanggan yang ditempel. */
export type PosGiftData = { data: StorefrontGiftData; slot: number };

export const EMPTY_POS_GIFT_DATA: PosGiftData = { data: EMPTY_GIFT_DATA, slot: 0 };

/**
 * Aturan hadiah yang saklar Kasirnya menyala, sedang berjalan, dan mencakup
 * produk keranjang Kasir — beserta produk keranjang, produk & varian AKTIF
 * hadiah, `option_ids` varian keranjang produk syarat produk tunggal, sisa
 * kuota (`admin_gift_rule_quota`: total + jatah pelanggan yang ditempel), dan
 * slot level pelanggan itu (grup NONAKTIF = pelanggan umum, cermin
 * `promo_customer_slot`). Sesi ADMIN; toko tak ter-resolusi = tanpa hadiah
 * (aturan A2). Galat dilempar — Kasir tetap jalan tanpa hadiah.
 */
export async function fetchPosGiftData(input: AdminGiftDataInput): Promise<PosGiftData> {
  return fetchAdminGiftData(input, true);
}

type AdminGiftDataInput = {
  productIds: readonly string[];
  variantIds: readonly string[];
  customer: { customerId: string; groupId: string | null } | null;
  now?: Date;
};

/**
 * Bahan pratinjau pembelian berhadiah BARU di Edit Variasi admin (H59, mig
 * 8880): sama dengan `fetchPosGiftData` tanpa saklar Kasir — semua aturan
 * aktif & berjalan yang mencakup produk itu, sisa kuota pelanggan, dan slot
 * level-nya. Sesi ADMIN; galat dilempar (pratinjaunya saja yang hilang).
 */
export async function fetchAdminOrderGiftData(input: AdminGiftDataInput): Promise<PosGiftData> {
  return fetchAdminGiftData(input, false);
}

async function fetchAdminGiftData(input: AdminGiftDataInput, posOnly: boolean): Promise<PosGiftData> {
  const wanted = [...new Set(input.productIds.filter(Boolean))];
  if (!wanted.length) return EMPTY_POS_GIFT_DATA;
  const storeId = getClientStoreId();
  if (!storeId) return EMPTY_POS_GIFT_DATA;
  const db = createBrowserSupabase("admin");
  if (!db) return EMPTY_POS_GIFT_DATA;
  const now = input.now ?? new Date();

  let ruleQuery = db.from("gift_rules").select(GIFT_RULE_SELECT).eq("store_id", storeId).eq("is_active", true);
  if (posOnly) ruleQuery = ruleQuery.eq("apply_to_pos", true);
  const { data: ruleRows, error: ruleErr } = await ruleQuery;
  if (ruleErr) throw new Error(ruleErr.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const running = ((ruleRows as any[]) ?? [])
    .map(mapGiftRuleRow)
    .filter((r) => r.storeId === storeId && (!posOnly || r.applyToPos) && isGiftRuleRunning(r, now));
  if (!running.length) return EMPTY_POS_GIFT_DATA;

  const products = new Map<string, GiftProductInfo>();
  const loadProducts = async (ids: readonly string[]) => {
    for (let i = 0; i < ids.length; i += 150) {
      const { data: rows, error } = await db
        .from("products")
        .select(GIFT_PRODUCT_SELECT)
        .in("id", ids.slice(i, i + 150))
        .eq("store_id", storeId);
      if (error) throw new Error(error.message);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const row of (rows as any[]) ?? []) {
        const p = mapGiftProduct(row);
        products.set(p.id, p);
      }
    }
  };
  await loadProducts(wanted);
  const cartRefs: GiftProductRef[] = wanted
    .map((id) => products.get(id))
    .filter((p): p is GiftProductInfo => Boolean(p))
    .map((p) => ({ id: p.id, brand: p.brand, category: p.category, productType: p.productType }));
  const rules = running
    .filter((r) => cartRefs.some((ref) => giftRuleCoversProduct(r, ref)))
    .sort((a, b) => a.name.localeCompare(b.name, "id"));
  if (!rules.length || !rules.some((r) => r.rewardProductId)) return EMPTY_POS_GIFT_DATA;

  const rewardIds = [...new Set(rules.map((r) => r.rewardProductId).filter((x): x is string => Boolean(x)))];
  await loadProducts(rewardIds.filter((id) => !products.has(id)));
  // Sesi admin membaca varian NONAKTIF juga — disaring di sini (server menolaknya).
  const { data: rewardRows, error: rewardErr } = await db
    .from("product_variants")
    .select(GIFT_VARIANT_SELECT)
    .in("product_id", rewardIds)
    .eq("store_id", storeId)
    .eq("is_active", true)
    .order("sort_order", { ascending: true })
    .limit(GIFT_VARIANT_CAP);
  if (rewardErr) throw new Error(rewardErr.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const v of (rewardRows as any[]) ?? []) {
    const p = products.get(String(v.product_id));
    if (p) p.variants.push(mapGiftRewardVariant(v, p.productType));
  }

  // `option_ids` varian keranjang produk syarat PRODUK TUNGGAL (batas varian).
  const variantOptionIds = new Map<string, string[]>();
  const cartIds = [...new Set(input.variantIds.filter(Boolean))];
  const triggerSet = new Set(
    rules.filter((r) => r.triggerScope !== "target" && r.triggerProductId).map((r) => r.triggerProductId as string)
  );
  for (let i = 0; triggerSet.size && i < cartIds.length; i += 200) {
    const { data: rows, error } = await db
      .from("product_variants")
      .select("id, product_id, option_ids")
      .in("id", cartIds.slice(i, i + 200))
      .eq("store_id", storeId);
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const v of (rows as any[]) ?? []) {
      if (!triggerSet.has(String(v.product_id))) continue;
      variantOptionIds.set(String(v.id), Array.isArray(v.option_ids) ? v.option_ids.map(String) : []);
    }
  }

  const customerId = input.customer?.customerId ?? null;
  const quotas = new Map<string, GiftQuota>();
  const quotaIds = rules.filter(giftRuleHasQuota).map((r) => r.id);
  if (quotaIds.length) {
    const { data: quotaRows, error: quotaErr } = await db.rpc("admin_gift_rule_quota", {
      p_rule_ids: quotaIds,
      p_customer_id: customerId
    });
    if (quotaErr) throw new Error(quotaErr.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (quotaRows as any[]) ?? []) {
      const { ruleId, quota } = mapAdminGiftQuotaRow(row);
      if (ruleId) quotas.set(ruleId, quota);
    }
  }

  // Slot level = grup AKTIF pelanggan (cermin `promo_customer_slot`).
  let slot = 0;
  const groupId = input.customer?.groupId ?? null;
  if (customerId && groupId) {
    const { data: group, error: groupErr } = await db
      .from("reseller_groups")
      .select("slot, is_active")
      .eq("id", groupId)
      .eq("store_id", storeId)
      .maybeSingle();
    if (groupErr) throw new Error(groupErr.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = group as any;
    if (g?.is_active === true && Number.isFinite(Number(g.slot))) slot = Number(g.slot);
  }
  return { data: { rules, products, variantOptionIds, quotas }, slot };
}

export type AdminOrderGiftResult = { orderGiftId: string; qty: number; orderCode: string };

/**
 * Simpan Tambah Hadiah (`admin_add_order_gift`, izin `pesanan.kelola`). Galat
 * berkode hadiah diterjemahkan ke kalimat admin; selebihnya pesan server.
 */
export async function addOrderGiftAdmin(
  payload: NonNullable<ReturnType<typeof adminGiftPayload>>
): Promise<AdminOrderGiftResult> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { data, error } = await db.rpc("admin_add_order_gift", payload);
  if (error) {
    throw new Error(adminGiftErrorMessage(`${error.message}|${error.details ?? ""}`) ?? error.message);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = (data ?? {}) as any;
  return {
    orderGiftId: String(res.orderGiftId ?? ""),
    qty: Math.max(0, Number(res.qty) || 0),
    orderCode: String(res.orderCode ?? "")
  };
}

/**
 * Lengkapi Hadiah (`admin_fill_order_gift`, mig 8870, izin `pesanan.kelola`):
 * isi kekurangan hadiah yang dilaporkan mesin (kalimat "kurang N pcs"). Galat
 * berkode hadiah diterjemahkan ke kalimat admin; selebihnya pesan server.
 */
export async function fillOrderGiftAdmin(orderGiftId: string): Promise<GiftFillResult> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { data, error } = await db.rpc("admin_fill_order_gift", { p_order_gift_id: orderGiftId });
  if (error) {
    throw new Error(adminGiftErrorMessage(`${error.message}|${error.details ?? ""}`) ?? error.message);
  }
  return mapGiftFillResult(data);
}

/**
 * Semua aturan hadiah toko (aktif & tidak). RLS admin memakai cabang escape
 * `current_store_id() is null or …` — disaring ke toko Host (idiom bundel).
 */
export async function fetchGiftRulesAdmin(): Promise<GiftRule[]> {
  const db = createBrowserSupabase("admin");
  if (!db) return [];
  const { data, error } = await db
    .from("gift_rules")
    .select(GIFT_RULE_SELECT)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows = ((data as any[]) ?? []).map(mapGiftRuleRow);
  const hostStore = getClientStoreId();
  return hostStore ? rows.filter((r) => r.storeId === hostStore) : rows;
}

export async function fetchGiftRuleAdmin(id: string): Promise<GiftRule | null> {
  const db = createBrowserSupabase("admin");
  if (!db) return null;
  const { data, error } = await db.from("gift_rules").select(GIFT_RULE_SELECT).eq("id", id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapGiftRuleRow(data) : null;
}

/** Simpan (buat / ubah) — RPC ber-izin `produk.hadiah`; H5 ditegakkan server. */
export async function saveGiftRule(payload: Record<string, unknown>): Promise<string> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { data, error } = await db.rpc("admin_save_gift_rule", { p_rule: payload });
  if (error) throw new Error(error.message);
  return String(data);
}

export async function setGiftRuleActive(id: string, isActive: boolean): Promise<void> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { error } = await db.rpc("admin_set_gift_rule_status", { p_rule_id: id, p_active: isActive });
  if (error) throw new Error(error.message);
}

export async function deleteGiftRule(id: string): Promise<void> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { error } = await db.rpc("admin_delete_gift_rule", { p_rule_id: id });
  if (error) throw new Error(error.message);
}

/** Pratinjau editor (mig 8780): produk yang tercakup syarat saat ini + bentrok H24 pertama. */
export type GiftRulePreview = { coveredProducts: number; clashRule: string | null; clashProduct: string | null };

/**
 * `admin_gift_rule_preview` — dinilai atas katalog toko di server (cakupan
 * kategori/merek BY NAMA, produk yang bisa dipesan). Muatan = bentuk
 * `giftDraftToPayload` (kunci yang tak dikenal diabaikan).
 */
export async function previewGiftRule(payload: Record<string, unknown>): Promise<GiftRulePreview> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { data, error } = await db.rpc("admin_gift_rule_preview", { p_rule: payload });
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const row = ((data as any[]) ?? [])[0] ?? {};
  return {
    coveredProducts: Math.max(0, Number(row.covered_products) || 0),
    clashRule: row.clash_rule ? String(row.clash_rule) : null,
    clashProduct: row.clash_product ? String(row.clash_product) : null
  };
}

/** Aturan AKTIF yang sedang bentrok (H24) — termasuk bentrok belakangan; satu baris per aturan. */
export async function fetchGiftRuleClashes(): Promise<Map<string, { otherName: string; productName: string }>> {
  const out = new Map<string, { otherName: string; productName: string }>();
  const db = createBrowserSupabase("admin");
  if (!db) return out;
  const { data, error } = await db.rpc("admin_gift_rule_clashes");
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const r of (data as any[]) ?? []) {
    if (!r?.rule_id) continue;
    out.set(String(r.rule_id), { otherName: String(r.other_name ?? ""), productName: String(r.product_name ?? "") });
  }
  return out;
}

/** Pcs hadiah hidup & jumlah pembelian per aturan. */
export async function fetchGiftRulesOverview(): Promise<Map<string, { giftPcs: number; purchases: number }>> {
  const out = new Map<string, { giftPcs: number; purchases: number }>();
  const db = createBrowserSupabase("admin");
  if (!db) return out;
  const { data, error } = await db.rpc("admin_gift_rules_overview");
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const r of (data as any[]) ?? []) {
    if (!r?.rule_id) continue;
    out.set(String(r.rule_id), { giftPcs: Number(r.gift_pcs) || 0, purchases: Number(r.purchases) || 0 });
  }
  return out;
}

export type GiftRuleAuditEntry = {
  id: string;
  action: "create" | "update" | "status" | "delete";
  actorName: string | null;
  changes: Record<string, unknown>;
  createdAt: string;
};

export async function fetchGiftRuleAuditLog(ruleId: string): Promise<GiftRuleAuditEntry[]> {
  const db = createBrowserSupabase("admin");
  if (!db) return [];
  const { data, error } = await db
    .from("gift_rule_audit_log")
    .select("id, action, actor_name, changes, created_at")
    .eq("rule_id", ruleId)
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map((r) => ({
    id: String(r.id),
    action: r.action,
    actorName: r.actor_name ?? null,
    changes: (r.changes && typeof r.changes === "object" ? r.changes : {}) as Record<string, unknown>,
    createdAt: String(r.created_at ?? "")
  }));
}

/**
 * Nama + keadaan jual produk untuk kartu daftar aturan hadiah. `optionIds` =
 * opsi varian hidup — bahan badge "batas varian perlu dipilih ulang".
 */
export async function fetchGiftListProducts(ids: readonly string[]): Promise<Map<string, GiftListProduct>> {
  const out = new Map<string, GiftListProduct>();
  const unique = [...new Set(ids.filter(Boolean))];
  if (!unique.length) return out;
  const db = createBrowserSupabase("admin");
  if (!db) return out;
  for (let i = 0; i < unique.length; i += 150) {
    const { data, error } = await db
      .from("products")
      .select(
        "id, name, is_active, status, product_type, variant_groups(id, sort_order, variant_options(id, name, sort_order))"
      )
      .in("id", unique.slice(i, i + 150));
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (data as any[]) ?? []) {
      const optionIds = new Set<string>();
      const optionNames = new Map<string, string>();
      const bySort = (a: { sort_order?: unknown }, b: { sort_order?: unknown }) =>
        (Number(a?.sort_order) || 0) - (Number(b?.sort_order) || 0);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const g of ((row.variant_groups as any[]) ?? []).slice().sort(bySort)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const o of ((g?.variant_options as any[]) ?? []).slice().sort(bySort)) {
          optionIds.add(String(o.id));
          optionNames.set(String(o.id), String(o.name ?? "").trim());
        }
      }
      out.set(String(row.id), {
        name: String(row.name ?? "").trim(),
        isActive: row.is_active !== false,
        status: String(row.status ?? ""),
        productType: row.product_type === "preorder" ? "preorder" : "ready_stock",
        optionIds,
        optionNames
      });
    }
  }
  return out;
}

/** Struktur varian satu produk untuk editor aturan hadiah (sesi admin). */
export async function fetchGiftEditorProduct(productId: string): Promise<GiftProductFacts | null> {
  const db = createBrowserSupabase("admin");
  if (!db) return null;
  const { data, error } = await db
    .from("products")
    .select(
      "id, name, product_type, status, is_manual, is_active, variant_groups(id, name, sort_order, variant_options(id, name, sort_order)), product_variants(id, name, option_ids, price_extra, stock, is_active, sort_order)"
    )
    .eq("id", productId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const row = data as any;
  const productType = row.product_type === "preorder" ? "preorder" : "ready_stock";
  const bySort = (a: { sort_order?: unknown }, b: { sort_order?: unknown }) =>
    (Number(a?.sort_order) || 0) - (Number(b?.sort_order) || 0);
  return {
    id: String(row.id),
    name: String(row.name ?? "").trim(),
    productType,
    status: String(row.status ?? ""),
    isManual: row.is_manual === true,
    isActive: row.is_active !== false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    groups: ((row.variant_groups as any[]) ?? [])
      .slice()
      .sort(bySort)
      .map((g) => ({
        id: String(g.id),
        name: String(g.name ?? "").trim(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options: ((g.variant_options as any[]) ?? [])
          .slice()
          .sort(bySort)
          .map((o) => ({ id: String(o.id), name: String(o.name ?? "").trim() }))
      })),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variants: ((row.product_variants as any[]) ?? [])
      .slice()
      .sort(bySort)
      .map((v) => ({
        id: String(v.id),
        name: String(v.name ?? "").trim() || "Standar",
        optionIds: Array.isArray(v.option_ids) ? v.option_ids.map(String) : [],
        priceExtra: Math.max(0, Math.round(Number(v.price_extra) || 0)),
        stock: productType === "ready_stock" ? Math.max(0, Math.floor(Number(v.stock) || 0)) : null,
        isActive: v.is_active !== false
      }))
  };
}
